import React, { useState, useEffect } from 'react';
import './SchufaCheckView.less';
import { FormikValues } from 'formik';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Autocomplete,
  CircularProgress,
  Paper,
} from '@mui/material';
import Box from '@mui/material/Box';
import NavigationBar from '../../../components/NavigationBar/NavigationBar';
import BrandButton from '../../../components/BrandButton/BrandButton';
import {
  getBrandColor,
  isLocalizeAdded,
  LocalizePropType,
} from 'getaway-data-layer';
import { GA_TITLES, isDashboardScreen } from '../../../../utils';
import Hyperlink from 'react-native-hyperlink';
// @ts-ignore
import { Linking } from 'react-native-web';
import Markdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import Flag from 'react-world-flags';

type Props = {
  localize: LocalizePropType;
  supportedCountries: Array<any>;
  formik: FormikValues;
  // licenseStatus: string;
  // customError: string;
  // minDate: any;
  // maxDate: any;
  onClose: () => void;
  country: string;
  onCountryChange: (value: any) => void;
  // dateFormat: string;
  onSearchAddressClicked: () => void;
  onLocationSelected: (location: any) => void;
};

const SchufaCheckView = ({
  localize,
  supportedCountries,
  formik,
  // customError,
  // minDate,
  // maxDate,
  // licenseStatus,
  onClose,
  onCountryChange,
  // dateFormat,
  onSearchAddressClicked,
  onLocationSelected,
}: Props) => {
  const [addressOptions, setAddressOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const getAddressDetails = (details: Record<string, any>): any => {
    const { address_components: addressComponents } = details;
    const address = {
      streetName: '',
      streetNumber: '',
      city: '',
      country: '',
      postalCode: '',
    };

    if (addressComponents) {
      addressComponents.forEach(
        (component: {
          types: any[];
          short_name: string;
          long_name: string;
        }) => {
          switch (component.types[0]) {
            case 'street_number':
              address.streetNumber = component.short_name;
              break;
            case 'route':
              address.streetName = component.short_name;
              break;
            case 'locality':
              address.city = component?.long_name || component.short_name;
              break;
            case 'country':
              address.country = component.short_name;
              break;
            case 'postal_code':
              address.postalCode = component.short_name;
              break;
            default:
              break;
          }
        }
      );
    }
    onLocationSelected(address);
    return address;
  };

  const searchAddresses = async (input: string) => {
    if (!input || input.length < 2 || !window.google) return;

    setLoading(true);
    const service = new window.google.maps.places.AutocompleteService();

    try {
      const results = await new Promise((resolve, reject) => {
        service.getPlacePredictions(
          {
            input,
            componentRestrictions: { country: formik.values.country || 'DE' },
            types: ['address'],
          },
          (predictions, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK) {
              resolve(predictions || []);
            } else {
              resolve([]);
            }
          }
        );
      });

      setAddressOptions(results);
    } catch (error) {
      setAddressOptions([]);
    }
    setLoading(false);
  };

  const handleAddressSelect = (prediction: any) => {
    if (!prediction || !window.google) return;

    const map = new window.google.maps.Map(document.createElement('div'));
    const service = new window.google.maps.places.PlacesService(map);

    service.getDetails(
      {
        placeId: prediction.place_id,
      },
      (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          const addressData = getAddressDetails(place);
          setInputValue(addressData.streetName);
        }
      }
    );
  };

  const handleInputChange = (event, value) => {
    setInputValue(value);
    formik.setFieldValue('streetName', value);
    searchAddresses(value);
  };

  useEffect(() => {
    if (formik.values.streetName && !inputValue) {
      setInputValue(formik.values.streetName);
    }
  }, [formik.values.streetName]);

  return (
    <div className="screen-container">
      <NavigationBar
        TitleTestId="ShufaCheckView-title"
        title={localize('schufa.title')}
        description={localize('schufa.instruction')}
        showHelp
        showArrowBack={isDashboardScreen()}
        onClose={onClose}
      />
      <div
        className={
          isLocalizeAdded(localize, 'schufa.faq')
            ? 'screen-body-faq'
            : 'screen-body'
        }
      >
        <div
          className={
            isLocalizeAdded(localize, 'schufa.faq')
              ? 'screen-body-content-faq'
              : 'screen-body-content'
          }
        >
          {/*<div*/}
          {/*  data-testid="ShufaCheck-searchAddress"*/}
          {/*  className="update-address-search-container"*/}
          {/*  onClick={onSearchAddressClicked}*/}
          {/*>*/}
          {/*  <BrandIcon*/}
          {/*    style={{ width: 14, height: 14, marginRight: 16, marginLeft: 8 }}*/}
          {/*    name="search"*/}
          {/*  />*/}
          {/*  <div className="update-address-search-text">*/}
          {/*    {localize('firstSteps.address.search.text')}*/}
          {/*  </div>*/}
          {/*</div>*/}
          <Box component="form" noValidate onSubmit={formik.handleSubmit}>
            <div className="street-name-autocomplete-container">
              <Autocomplete
                freeSolo
                disableClearable
                options={addressOptions}
                loading={loading}
                inputValue={inputValue}
                onInputChange={handleInputChange}
                onChange={(event, value) => {
                  if (value && typeof value === 'object') {
                    handleAddressSelect(value);
                  }
                }}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  return option?.description || '';
                }}
                filterOptions={(options) => options}
                renderOption={(props, option) => (
                  <li {...props} key={option.place_id}>
                    {option.structured_formatting?.main_text}
                    {option.structured_formatting?.secondary_text?.split(
                      ','
                    )?.[0] && ', '}
                    {
                      option.structured_formatting?.secondary_text?.split(
                        ','
                      )[0]
                    }
                  </li>
                )}
                ListboxComponent={({ children, ...props }) => (
                  <ul {...props} style={{ padding: 0, margin: 0 }}>
                    {addressOptions.length > 0 && (
                      <li
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '8px 16px',
                          borderBottom: '1px solid #f8f8f8',
                          backgroundColor: '#f8f8f8',
                          fontWeight: 'bold',
                          fontSize: '14px',
                          color: '#333',
                          cursor: 'default',
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setAddressOptions([]);
                        }}
                      >
                        <span>Vorschläge</span>
                        <img
                          src="/images/closeIconBlack.png"
                          alt="powered by Google"
                          style={{
                            width: 16,
                            height: 16,
                            cursor: 'pointer',
                          }}
                        />
                      </li>
                    )}
                    <div style={{ marginTop: 8 }}>{children}</div>
                    {addressOptions.length > 0 && (
                      <li
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                        }}
                      >
                        <img
                          src="/images/google.png"
                          alt="powered by Google"
                          style={{
                            width: 120,
                            height: 40,
                            objectFit: 'contain',
                            alignSelf: 'center',
                          }}
                        />
                      </li>
                    )}
                  </ul>
                )}
                PaperComponent={({ children, ...props }) => (
                  <Paper
                    {...props}
                    style={{ width: 'calc(100% + 120px)', ...props.style }}
                  >
                    {children}
                  </Paper>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    data-testid="SchufaCheckView-streetNameInput"
                    required
                    // sx={{ width: 0.7, pr: 1 }}
                    label={localize(
                      'firstSteps.address.streetName.placeholder'
                    )}
                    helperText={
                      (formik.touched.streetName && formik.errors.streetName) ||
                      ' '
                    }
                    error={
                      formik.touched.streetName &&
                      Boolean(formik.errors.streetName)
                    }
                    variant="standard"
                    size="small"
                    autoComplete="address-line1"
                    name="streetName"
                    onBlur={formik.handleBlur}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </div>
            <TextField
              data-testid="SchufaCheckView-streetNumberInput"
              required
              id="streetNumber"
              sx={{ width: 0.3 }}
              label={localize('firstSteps.address.streetNum.placeholder')}
              helperText={
                (formik.touched.streetNumber && formik.errors.streetNumber) ||
                ' '
              }
              error={
                formik.touched.streetNumber &&
                Boolean(formik.errors.streetNumber)
              }
              variant="standard"
              size="small"
              type="text"
              {...formik.getFieldProps('streetNumber')}
            />
            <TextField
              data-testid="SchufaCheckView-postalCodeInput"
              required
              id="postalCode"
              sx={{ width: 0.3 }}
              label={localize('firstSteps.address.postalCode.placeholder')}
              helperText={
                (formik.touched.postalCode && formik.errors.postalCode) || ' '
              }
              error={
                formik.touched.postalCode && Boolean(formik.errors.postalCode)
              }
              variant="standard"
              size="small"
              type="text"
              autoComplete="postal-code"
              {...formik.getFieldProps('postalCode')}
            />
            <TextField
              data-testid="SchufaCheckView-cityInput"
              required
              id="city"
              sx={{ width: 0.7, pl: 1 }}
              label={localize('firstSteps.address.city.placeholder')}
              helperText={(formik.touched.city && formik.errors.city) || ' '}
              error={formik.touched.city && Boolean(formik.errors.city)}
              variant="standard"
              size="small"
              type="text"
              autoComplete="address-level2"
              {...formik.getFieldProps('city')}
            />
            <FormControl fullWidth required variant="standard">
              <InputLabel id="country-label">
                {localize('firstSteps.address.country.placeholder')}
              </InputLabel>
              <Select
                fullWidth
                required
                labelId="select-country"
                id="country"
                variant="standard"
                label={localize('firstSteps.address.country.placeholder')}
                {...formik.getFieldProps('country')}
                onChange={(event) => {
                  onCountryChange(event.target.value);
                  formik.handleChange(event);
                }}
                renderValue={(selected) => {
                  const selectedCountry = supportedCountries.find(
                    (item) => item.value === selected
                  );
                  if (!selectedCountry) return null;
                  return (
                    <div
                      style={{ display: 'flex', alignItems: 'center', gap: 16 }}
                    >
                      <Flag code={selectedCountry.value} height={12} />
                      {selectedCountry.text}
                    </div>
                  );
                }}
              >
                {supportedCountries.map((item) => (
                  <MenuItem
                    key={item.value}
                    value={item.value}
                    style={{ gap: 16 }}
                  >
                    <Flag code={item.value} height={12} />
                    {item.text}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <div>
              <img
                className="add-sepa-logo"
                src="/images/trustLogos.png"
                alt="add-sepa-logo"
              />
            </div>
            <div className="add-sepa-footnote">
              <Hyperlink
                linkStyle={{ color: getBrandColor() }}
                linkText={(url) =>
                  url === localize('settings.payment.footnote.url')
                    ? localize('settings.payment.footnote.url.text')
                    : url
                }
                onPress={(url) => {
                  Linking.openURL(url);
                }}
              >
                {localize(
                  'settings.payment.footnote',
                  localize('settings.payment.footnote.url')
                )}
              </Hyperlink>
            </div>
            <BrandButton
              id={GA_TITLES.ON_BOARDING.SUBMIT_SCHUFA}
              title={localize('schufa.button.title')}
              formik={formik}
            />
          </Box>
        </div>
        {isLocalizeAdded(localize, 'schufa.faq') && (
          <div className="faq">
            <Markdown rehypePlugins={[rehypeRaw]}>
              {localize('schufa.faq')}
            </Markdown>
          </div>
        )}
        <div className="screen-vertical-space" />
      </div>
    </div>
  );
};

export default SchufaCheckView;
